import React from 'react'

import pins from './pinsV2'
import config from 'constants/config'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import styled, { keyframes } from 'styled-components'
import PinHeart from 'tripPlanner/components/TripMaps/MapElement/PinAccessory/PinHeart'
import { rem } from 'polished'
import cn from 'clsx'

const PinHeartContainer = styled.div`
  position: absolute;
  z-index: 99;

  // flash and default have same positioning for pin heart
  bottom: ${rem(40)};
  left: ${rem(8)};

  &.isHovered, &.active {
    bottom: ${rem(44)};
    left: ${rem(12)};
  }

  &.soldOut {
    top: ${rem(-24)};
    left: ${rem(6)};

    &.isHovered, &.active {
      top: ${rem(-28)};
      left: ${rem(8)};
    }
  }

  &.isAllBedbank {
    bottom: ${rem(28)};
    left: ${rem(6)};

    &.isHovered, &.active {
      bottom: ${rem(32)};
      left: ${rem(8)};
    }
  }
`

const pinGrowAnimation = keyframes`
  from {
    transform: scale(0.5);
  }
  to {
    transform: scale(1);
  }
`

const MarkerContainer = styled.div`
  filter: drop-shadow(0px 3px 3px rgba(0, 0, 0, 0.4));
  position: relative;
`

const PinImage = styled.img`
  position: absolute;

  &.animate {
    animation: ${pinGrowAnimation} 0.2s ease-out;
    transform-origin: bottom center;
  }
`

interface Props {
  active: boolean
  visited: boolean
  soldOut: boolean
  marker: App.MapMarkerV2
  isHovered?: boolean
  isSaved?: boolean
  animateMarker?: boolean
}

function PropertyMarker(props: React.PropsWithChildren<Props>) {
  const {
    active,
    visited,
    soldOut,
    isSaved,
    marker,
    isHovered,
    animateMarker = false,
  } = props

  const isAllBedbank = marker.offers.every(offer => offer.type === OFFER_TYPE_BED_BANK)

  const isAllFlash = marker.offers.every(offer => offer.type === OFFER_TYPE_HOTEL)

  const brandPinConfig = pins[config.BRAND] ?? pins.default

  let pinIcons = brandPinConfig.pinIcons
  if (soldOut && isAllFlash) {
    pinIcons = brandPinConfig.flashSoldOutPinIcons
  } else if (soldOut) {
    pinIcons = brandPinConfig.soldOutPinIcons
  } else if (isAllBedbank) {
    pinIcons = brandPinConfig.bedbankPinIcons
  } else if (isAllFlash) {
    pinIcons = brandPinConfig.flashPinIcons
  }

  let pinVersion = pinIcons.default
  if (active || isHovered) {
    pinVersion = pinIcons.active
  } else if (visited) {
    pinVersion = pinIcons.previewed
  }

  const pin = (isHovered && !active) ? pinVersion.hover : pinVersion.default
  const { image, sizing } = pin

  const [width, height] = sizing.size

  return (
    <MarkerContainer>
      {
        isSaved && (
          <PinHeartContainer className={cn({ isAllBedbank, isAllFlash, soldOut, isHovered, active })}>
            <PinHeart />
          </PinHeartContainer>
        )
      }
      <PinImage
        className={cn('map-pin', { animate: animateMarker })}
        src={image}
        alt=""
        style={{
          height: rem(height),
          width: rem(width),
          left: rem(-sizing.anchor[0]),
          top: rem(-sizing.anchor[1]),
        }}
      />
    </MarkerContainer>
  )
}

export default React.memo(PropertyMarker)
